import { useEffect } from "react";

/**
 * GlobalSyncAnimator
 *
 * Single document-level click listener that adds a one-shot click animation
 * (720° icon spin + primary-coloured pulse ring) on every refresh/sync
 * button across the app — zero per-file changes.
 *
 * Detection:
 *  - Button (or its closest button ancestor) contains a lucide
 *    `lucide-refresh-*` or `lucide-rotate-*` icon, OR
 *  - Button has a `data-sync` attribute (opt-in for non-Refresh icons).
 *
 * Toasts are intentionally NOT handled here — each page knows what its
 * sync result means ("Fetched 20 of 1,234 entries" vs "Comments synced
 * — 5 new") and shows its own `toast(...)` from `@/hooks/use-toast` in its
 * sync handler. The spinner stops when the underlying `isPending` /
 * `loading` state flips, which is already wired everywhere.
 *
 * Skipped:
 *  - Disabled buttons.
 *  - Icon already has `animate-spin` (sync in progress; one-shot would
 *    fight the continuous spin).
 */
export function GlobalSyncAnimator() {
  useEffect(() => {
    const ANIMATION_MS = 800;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;

      const button = target.closest('button, [role="button"]') as HTMLElement | null;
      if (!button) return;
      if (button.hasAttribute("disabled")) return;

      const icon =
        (button.querySelector('[class*="lucide-refresh"]') as SVGElement | null) ||
        (button.querySelector('[class*="lucide-rotate"]') as SVGElement | null) ||
        (button.dataset.sync !== undefined
          ? (button.querySelector("svg") as SVGElement | null)
          : null);

      if (!icon) return;
      if (icon.classList.contains("animate-spin")) return;
      if (icon.classList.contains("sync-icon-clicked")) return;

      icon.classList.add("sync-icon-clicked");
      button.classList.add("sync-button-clicked");

      window.setTimeout(() => {
        icon.classList.remove("sync-icon-clicked");
        button.classList.remove("sync-button-clicked");
      }, ANIMATION_MS);
    };

    document.addEventListener("click", handleClick, { capture: true });
    return () => {
      document.removeEventListener("click", handleClick, { capture: true } as EventListenerOptions);
    };
  }, []);

  return null;
}
